import {
  CreditCard,
  Download,
  Monitor,
  Moon,
  PanelBottom,
  PanelRight,
  Settings,
  Sparkles,
  Sun,
  UserRound,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { authApi, useUserInfo } from "@/modules/auth";
import { EducationSubscriptionSection } from "@/modules/education/education-subscription-section";
import { usePrivacySession } from "@/modules/mine/privacy/use-privacy-session";
import { useSubscriptionOverviewQuery } from "@/modules/subscription";
import { SubscriptionOrders } from "@/modules/subscription/subscription-orders";
import logo from "@/shared/assets/images/logo.png";
import type { DialogComponentProps } from "@/shared/components/dialog";
import { useDialog } from "@/shared/components/dialog";
import { THEME_OPTIONS } from "@/shared/constants/theme-options";
import { cn } from "@/shared/lib/utils";
import {
  useColorMode,
  useTheme,
  useThemeActions,
} from "@/shared/store/theme-store";
import type { Mode } from "@/shared/types/theme";
import { Avatar, AvatarFallback, AvatarImage } from "@/shared/ui/avatar";
import { Button } from "@/shared/ui/button";
import { DialogDescription, DialogTitle } from "@/shared/ui/dialog";
import { ScrollArea } from "@/shared/ui/scroll-area";
import { Skeleton } from "@/shared/ui/skeleton";

import { AccountActionDialog } from "./account/account-action-dialog";
import { AccountConnections } from "./account/account-connections";
import { DataExportSection } from "./data-export/data-export-section";
import {
  usePreviewPlacement,
  useSettingsActions,
  type PreviewPlacement,
} from "./store";

type AccountSection = "account" | "data";
type SettingsSection = "general" | AccountSection | "subscription";

interface SettingsDialogProps {
  onOpenPricing: () => void;
  onOpenEducation: () => void;
  onUnlockPrivacy: () => Promise<boolean>;
  onManagePrivacy: () => Promise<boolean>;
  onLockPrivacy: () => void;
  onAccountDeleted: () => void;
}

const SECTIONS = [
  { value: "general", label: "常规", icon: Settings },
  { value: "account", label: "账户", icon: UserRound },
  { value: "data", label: "数据导出", icon: Download },
  { value: "subscription", label: "订阅", icon: CreditCard },
] as const;

const COLOR_MODES: Array<{
  value: Mode;
  label: string;
  icon: typeof Sun;
}> = [
  { value: "light", label: "浅色", icon: Sun },
  { value: "dark", label: "深色", icon: Moon },
  { value: "system", label: "跟随系统", icon: Monitor },
];

const LANGUAGE_OPTIONS = [
  { label: "简体中文", value: "zh-CN" },
  { label: "繁體中文", value: "zh-TW" },
  { label: "English", value: "en-US" },
] as const;

const PREVIEW_PLACEMENT_OPTIONS: Array<{
  value: PreviewPlacement;
  label: string;
  icon: typeof PanelRight;
}> = [
  { value: "right", label: "右侧", icon: PanelRight },
  { value: "bottom", label: "底部", icon: PanelBottom },
];

function SettingRow({
  title,
  description,
  children,
  titleWeight = "medium",
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
  titleWeight?: "normal" | "medium";
}) {
  return (
    <div className="flex flex-col gap-4 py-5 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <p className={cn("text-sm", titleWeight === "medium" ? "font-medium" : "font-normal")}>{title}</p>
        {description ? (
          <p className="mt-1 text-xs leading-5 text-muted-foreground">
            {description}
          </p>
        ) : null}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

function GeneralSettings() {
  const { i18n } = useTranslation();
  const theme = useTheme();
  const colorMode = useColorMode();
  const previewPlacement = usePreviewPlacement();
  const { setColorMode, setTheme } = useThemeActions();
  const { setPreviewPlacement } = useSettingsActions();

  return (
    <div>
      <div className="divide-y divide-border/50">
        <SettingRow title="外观" description="选择界面的明暗模式。">
          <div className="flex rounded-lg bg-muted p-1">
            {COLOR_MODES.map((mode) => {
              const Icon = mode.icon;
              const selected = colorMode === mode.value;

              return (
                <Button
                  key={mode.value}
                  type="button"
                  variant="ghost"
                  size="sm"
                  className={cn(
                    "h-7 gap-1.5 px-2.5 text-xs text-muted-foreground hover:bg-background/70",
                    selected &&
                      "bg-background text-foreground shadow-xs hover:bg-background",
                  )}
                  onClick={() => setColorMode(mode.value)}
                >
                  <Icon className="size-3.5" />
                  {mode.label}
                </Button>
              );
            })}
          </div>
        </SettingRow>

        <SettingRow title="主题色" description="用于按钮和强调信息的颜色。">
          <div className="flex flex-wrap justify-end gap-2">
            {THEME_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                aria-label={option.name}
                title={option.name}
                className={cn(
                  "flex size-7 items-center justify-center rounded-full border border-transparent transition-transform hover:scale-105",
                  theme === option.value && "border-foreground/30",
                )}
                onClick={() => setTheme(option.value)}
              >
                <span
                  className="size-4 rounded-full"
                  style={{ backgroundColor: option.color }}
                />
              </button>
            ))}
          </div>
        </SettingRow>

        <SettingRow title="语言" description="设置界面显示语言。">
          <div className="flex gap-1 rounded-lg bg-muted p-1">
            {LANGUAGE_OPTIONS.map((language) => {
              const selected = i18n.resolvedLanguage === language.value;

              return (
                <Button
                  key={language.value}
                  type="button"
                  variant="ghost"
                  size="sm"
                  className={cn(
                    "h-7 px-2.5 text-xs text-muted-foreground hover:bg-background/70",
                    selected &&
                      "bg-background text-foreground shadow-xs hover:bg-background",
                  )}
                  onClick={() => void i18n.changeLanguage(language.value)}
                >
                  {language.label}
                </Button>
              );
            })}
          </div>
        </SettingRow>

        <SettingRow
          title="详情预览"
          description="右侧模式会在窄屏覆盖显示，触屏设备自动使用底部弹框。"
        >
          <div className="flex rounded-lg bg-muted p-1">
            {PREVIEW_PLACEMENT_OPTIONS.map((option) => {
              const Icon = option.icon;
              const selected = previewPlacement === option.value;

              return (
                <Button
                  key={option.value}
                  type="button"
                  variant="ghost"
                  size="sm"
                  className={cn(
                    "h-7 gap-1.5 px-2.5 text-xs text-muted-foreground hover:bg-background/70",
                    selected &&
                      "bg-background text-foreground shadow-xs hover:bg-background",
                  )}
                  onClick={() => setPreviewPlacement(option.value)}
                >
                  <Icon className="size-3.5" />
                  {option.label}
                </Button>
              );
            })}
          </div>
        </SettingRow>
      </div>
    </div>
  );
}

function AccountSettings({
  section,
  onUnlockPrivacy,
  onManagePrivacy,
  onLockPrivacy,
  onAccountDeleted,
}: {
  section: AccountSection;
  onUnlockPrivacy: () => Promise<boolean>;
  onManagePrivacy: () => Promise<void>;
  onLockPrivacy: () => void;
  onAccountDeleted: () => void;
}) {
  const userInfo = useUserInfo();
  const avatarUrl = userInfo?.qq?.avatar_url ?? userInfo?.wechat?.avatar_url ?? logo;
  const dialog = useDialog();
  const { isUnlocked, expiresAt } = usePrivacySession();
  const [now, setNow] = useState(() => Date.now());
  const [sendingVerification, setSendingVerification] = useState(false);
  useEffect(() => {
    if (!isUnlocked || section !== "account") return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [isUnlocked, expiresAt, section]);
  const remainingSeconds = Math.max(0, Math.ceil(((expiresAt ?? 0) - now) / 1000));
  const remainingTime = `${Math.floor(remainingSeconds / 60)}:${String(remainingSeconds % 60).padStart(2, "0")}`;

  function openAccountAction(action: "nickname" | "email" | "phone" | "password") {
    const titles = { nickname: "修改昵称", email: "更换邮箱", phone: userInfo?.phone ? "更换手机号" : "绑定手机号", password: userInfo?.has_password ? "修改密码" : "设置密码" };
    void dialog.open(AccountActionDialog, {
      action,
      currentValue: action === "nickname" ? userInfo?.nickname : action === "email" ? userInfo?.email : userInfo?.phone,
      hasPassword: Boolean(userInfo?.has_password),
    }, { title: titles[action] });
  }

  async function deleteAccount() {
    const confirmed = await dialog.confirm({
      title: "注销账户",
      description: "注销后将立即无法登录，收藏及账户数据将在 7 天后清理。此操作无法在页面中撤销。确定继续吗？",
      confirmText: "确认注销",
      variant: "destructive",
      onConfirm: async () => { await authApi.deleteAccount(); },
    });
    if (!confirmed) return;
    dialog.closeAll();
    onAccountDeleted();
    toast.success("账户已注销");
  }

  async function resendVerification() {
    if (sendingVerification) return;
    setSendingVerification(true);
    try {
      await authApi.sendVerifyEmail();
      toast.success("验证邮件已发送，请查收邮箱");
    } catch {
      // 请求层展示接口错误。
    } finally {
      setSendingVerification(false);
    }
  }

  return (
    <>
      {section === "account" && <div className="pt-5">
        <div className="mb-8 flex items-center gap-4">
          <Avatar className="size-12 rounded-xl">
            <AvatarImage src={avatarUrl} alt={userInfo?.nickname || "账户头像"} />
            <AvatarFallback className="rounded-xl">CN</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="truncate text-base font-semibold">{userInfo?.nickname || "未设置昵称"}</p>
            <p className="truncate text-sm text-muted-foreground">{userInfo?.email || "未绑定邮箱"}</p>
          </div>
        </div>
        <h3 className="mb-3 text-sm font-medium">基本资料</h3>
        <div className="divide-y divide-border/50 rounded-xl bg-[#f9f9f9] px-4 dark:bg-muted/50">
          <SettingRow title="昵称" description="昵称修改暂未开放。" titleWeight="normal">
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">{userInfo?.nickname || "未设置昵称"}</span>
              <Button type="button" variant="outline" size="sm" className="font-normal" onClick={() => openAccountAction("nickname")}>编辑</Button>
            </div>
          </SettingRow>
          <SettingRow title="邮箱" description={userInfo?.email && !userInfo.email_verified ? "当前邮箱尚未验证。" : undefined} titleWeight="normal">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground">{userInfo?.email || "未绑定邮箱"}</span>
              {userInfo?.email && !userInfo.email_verified && (
                <Button type="button" variant="ghost" size="sm" className="font-normal" disabled={sendingVerification} onClick={() => void resendVerification()}>发送验证邮件</Button>
              )}
              <Button type="button" variant="outline" size="sm" className="font-normal" onClick={() => openAccountAction("email")}>更换邮箱</Button>
            </div>
          </SettingRow>
          <SettingRow title="手机号" titleWeight="normal">
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">{userInfo?.phone || "未绑定"}</span>
              <Button type="button" variant="outline" size="sm" className="font-normal" onClick={() => openAccountAction("phone")}>{userInfo?.phone ? "更换" : "绑定"}</Button>
            </div>
          </SettingRow>
        </div>
        <h3 className="mb-3 mt-8 text-sm font-medium">安全</h3>
        <div className="divide-y divide-border/50 rounded-xl bg-[#f9f9f9] px-4 dark:bg-muted/50">
          <SettingRow title="账户密码" titleWeight="normal">
            <Button type="button" variant="outline" size="sm" className="font-normal" onClick={() => openAccountAction("password")}>
              {userInfo?.has_password ? "修改密码" : "设置密码"}
            </Button>
          </SettingRow>
          <SettingRow title="隐私密码" description="用于解锁私密文件夹和下载受保护的数据。" titleWeight="normal">
            <Button type="button" variant="outline" size="sm" className="font-normal" onClick={() => void onManagePrivacy()}>
              {userInfo?.privacy_configured ? "重置隐私密码" : "设置隐私密码"}
            </Button>
          </SettingRow>
          {userInfo?.privacy_configured && (
            <SettingRow
              title="私密访问"
              titleWeight="normal"
              description={isUnlocked
                ? `当前标签页已解锁，可以查看私密文件夹；剩余 ${remainingTime}，届时自动锁定。`
                : "当前已锁定，查看私密文件夹时需要验证隐私密码。"}
            >
              {isUnlocked ? (
                <Button type="button" variant="outline" size="sm" onClick={onLockPrivacy}>
                  立即锁定
                </Button>
              ) : <span className="text-sm text-muted-foreground">已锁定</span>}
            </SettingRow>
          )}
        </div>
        <h3 className="mb-3 mt-8 text-sm font-medium">第三方账号</h3>
        {userInfo && <AccountConnections user={userInfo} />}
        {userInfo && (
          <div className="mt-8 rounded-xl bg-[#f9f9f9] p-4 dark:bg-muted/50">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-medium">注销账户</h3>
                <p className="mt-1 text-xs leading-5 text-muted-foreground">注销后将无法登录，账户数据会在 7 天后清理。</p>
              </div>
              <Button type="button" variant="outline" size="sm" className="font-normal text-destructive" onClick={() => void deleteAccount()}>注销账户</Button>
            </div>
          </div>
        )}
      </div>}

      {section === "data" && <div className="pt-5">
        {userInfo?.id && (
          <DataExportSection
            key={userInfo.id}
            userId={userInfo.id}
            onUnlockPrivacy={onUnlockPrivacy}
          />
        )}
      </div>}
    </>
  );
}

const SUBSCRIPTION_STATUS_LABELS: Record<string, string> = {
  active: "有效",
  canceled: "已取消",
  expired: "已到期",
  trialing: "试用中",
};

const BILLING_PERIOD_LABELS: Record<string, string> = {
  monthly: "按月订阅",
  yearly: "按年订阅",
};

function formatSubscriptionDate(value?: string) {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat("zh-CN", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date);
}

function SubscriptionSettings({
  close,
  onOpenPricing,
  onOpenEducation,
}: {
  close: () => void;
  onOpenPricing: () => void;
  onOpenEducation: () => void;
}) {
  const userId = useUserInfo()?.id ?? "";
  const { data, isError, isLoading, refetch } =
    useSubscriptionOverviewQuery(userId);
  const subscription = data?.subscription;
  const isFreePlan = !subscription || subscription.plan_code === "free";

  const expiresAt = formatSubscriptionDate(subscription?.expires_at);
  const statusLabel = isFreePlan
    ? "免费使用"
    : SUBSCRIPTION_STATUS_LABELS[subscription.status] ?? subscription.status;
  const periodLabel = subscription?.period
    ? BILLING_PERIOD_LABELS[subscription.period] ?? subscription.period
    : "永久免费";

  return (
    <div className="py-5">
      {isLoading ? (
        <div className="space-y-4 pb-5">
          <Skeleton className="h-28 w-full rounded-xl" />
          <Skeleton className="h-14 w-full" />
        </div>
      ) : isError ? (
        <div className="flex min-h-32 flex-col items-center justify-center text-center">
          <p className="text-sm font-medium">暂时无法获取订阅信息</p>
          <Button type="button" variant="outline" size="sm" className="mt-3" onClick={() => void refetch()}>
            重新加载
          </Button>
        </div>
      ) : (
      <>
      <div className="flex items-center justify-between gap-4 rounded-xl bg-[#f9f9f9] p-5 dark:bg-muted/50">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-background text-foreground shadow-xs">
            <Sparkles className="size-4" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className="truncate font-semibold">
                {subscription?.plan_name || "Free Plan"}
              </p>
              <span className="rounded-md bg-background px-2 py-0.5 text-xs text-muted-foreground">
                {statusLabel}
              </span>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {isFreePlan ? "当前使用免费套餐" : periodLabel}
            </p>
          </div>
        </div>
        <Button
          type="button"
          size="sm"
          onClick={() => {
            close();
            onOpenPricing();
          }}
        >
          升级套餐
        </Button>
      </div>

      <div className="mt-3 divide-y divide-border/50 px-1">
        <SettingRow title="计费周期">
          <span className="text-sm text-muted-foreground">{periodLabel}</span>
        </SettingRow>
        {!isFreePlan && expiresAt ? (
          <SettingRow title="有效期至">
            <span className="text-sm text-muted-foreground">{expiresAt}</span>
          </SettingRow>
        ) : null}
      </div>
      </>
      )}
      <EducationSubscriptionSection userId={userId} onNavigate={() => {
        close();
        onOpenEducation();
      }} />
      <SubscriptionOrders userId={userId} />
    </div>
  );
}

export function SettingsDialog({
  close,
  onOpenPricing,
  onOpenEducation,
  onUnlockPrivacy,
  onManagePrivacy,
  onLockPrivacy,
  onAccountDeleted,
}: SettingsDialogProps & DialogComponentProps) {
  const [section, setSection] = useState<SettingsSection>("general");
  const currentSection = SECTIONS.find((item) => item.value === section);

  async function managePrivacy() {
    try {
      await onManagePrivacy();
    } catch {
      // 请求层负责显示错误。
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col sm:flex-row">
      <aside className="shrink-0 border-b bg-muted/35 p-4 sm:w-52 sm:border-r sm:border-b-0">
        <DialogTitle className="px-2 py-1 text-base">设置</DialogTitle>
        <DialogDescription className="sr-only">
          管理常规偏好、账户数据与订阅信息。
        </DialogDescription>
        <nav aria-label="设置分类" className="mt-4 flex gap-1 overflow-x-auto sm:flex-col sm:overflow-visible">
          {SECTIONS.map((item) => {
            const Icon = item.icon;
            const selected = item.value === section;

            return (
              <button
                key={item.value}
                type="button"
                className={cn(
                  "flex h-9 shrink-0 items-center gap-2 whitespace-nowrap rounded-md px-3 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
                  selected && "bg-muted font-medium text-foreground",
                )}
                onClick={() => setSection(item.value)}
              >
                <Icon className="size-4" />
                {item.label}
              </button>
            );
          })}
        </nav>
      </aside>

      <section className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="flex h-16 shrink-0 items-center px-6 pr-12">
          <h2 className="font-semibold">{currentSection?.label}</h2>
        </header>
        <ScrollArea className="min-h-0 flex-1">
          <div className="px-6 pb-6">
            {section === "general" ? <GeneralSettings /> : null}
            {section !== "general" && section !== "subscription" ? (
              <AccountSettings
                section={section}
                onUnlockPrivacy={onUnlockPrivacy}
                onManagePrivacy={managePrivacy}
                onLockPrivacy={onLockPrivacy}
                onAccountDeleted={onAccountDeleted}
              />
            ) : null}
            {section === "subscription" ? (
              <SubscriptionSettings
                close={close}
                onOpenPricing={onOpenPricing}
                onOpenEducation={onOpenEducation}
              />
            ) : null}
          </div>
        </ScrollArea>
      </section>
    </div>
  );
}
